import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { servicesData } from '@/mocks/homepage';

const serviceRoutes: Record<string, string> = {
  refection: '/refection-toiture-levis',
  repair: '/reparation-toiture-levis',
  flat: '/toit-plat-membrane-elastomere',
  metal: '/toiture-tole-metal',
  inspection: '/inspection-toiture',
  snow: '/deneigement-toiture',
};

export default function Services() {
  const { t } = useTranslation('homepage');
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal<HTMLDivElement>(0.15);
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>(0.1);

  const navigateTo = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  return (
    <section id="services" className="bg-white py-20 md:py-28 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
        {/* Title */}
        <div
          ref={titleRef}
          className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="font-manrope font-bold text-slate-primary text-2xl md:text-3xl lg:text-4xl mb-4">
            {t('services_h2')}
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto">
            {t('services_subtitle')}
          </p>
        </div>

        {/* Image + Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left: Image */}
          <div
            className={`lg:col-span-4 transition-all duration-1000 delay-100 ${
              gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="rounded-lg overflow-hidden sticky top-24">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20roofing%20team%20working%20on%20a%20residential%20Quebec%20house%20roof%20during%20golden%20hour%2C%20couvreur%20installing%20asphalt%20shingles%20on%20sloped%20roof%2C%20safety%20harness%20and%20equipment%20visible%2C%20warm%20copper%20and%20slate%20tones%2C%20autumn%20trees%20in%20background%2C%20documentary%20photography%20style%2C%20no%20text%20visible&width=500&height=700&seq=home-serv1&orientation=portrait"
                alt="Équipe de couvreurs partenaires certifiés RBQ en action sur un chantier à Lévis"
                className="w-full h-80 lg:h-[520px] object-cover object-top"
                loading="lazy"
                width="500"
                height="700"
              />
              <div className="bg-slate-primary p-4">
                <p className="text-white/90 text-sm font-medium">
                  <i className="ri-team-line mr-2 text-copper" aria-hidden="true" />
                  Couvreurs certifiés à Lévis
                </p>
              </div>
            </div>
          </div>

          {/* Right: Grid */}
          <div
            ref={gridRef}
            className={`lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 transition-all duration-1000 delay-200 ${
              gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {servicesData.map((service, index) => {
              const route = serviceRoutes[service.id];
              return (
                <a
                  key={service.id}
                  href={route}
                  onClick={(e) => { e.preventDefault(); navigateTo(route); }}
                  aria-label={`${t(service.titleKey)} — ${t('service_link')}`}
                  className="group bg-soft-gray rounded-lg p-6 md:p-8 hover:shadow-lg transition-all duration-300 block"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-lg bg-slate-primary mb-5 group-hover:bg-copper transition-colors duration-300">
                    <i className={`${service.icon} text-white text-2xl`} aria-hidden="true" />
                  </div>
                  <h3 className="font-manrope font-bold text-slate-primary text-lg md:text-xl mb-3">
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-4">
                    {t(service.descKey)}
                  </p>
                  <span className="inline-flex items-center gap-1 text-copper font-medium text-sm group-hover:gap-2 transition-all duration-300">
                    {t('service_link')}
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}